import React from "react";
import PropTypes from "prop-types";
import { Icon } from "@qgiv/core-react";
import "./AccountRegistrationError.scss";

const AccountRegistrationError = ({
    Email = "",
    emailExistsInPreviousRegistration,
    emailExistsInCurrentRegistration,
    lastVerifiedEmail = "",
    Is_Nfp = false,
    isPrimaryRegistrant = true,
    Managed_By_Primary = false,
    yourFundraisingDashboard = "",
}) => {
    const needsToBeVerified = !Is_Nfp && !Managed_By_Primary;
    // Convert to lowercase before comparing as emails are not case sensitive
    const hasChangedSincePreviousRegistrationValidation =
        lastVerifiedEmail.toLowerCase() !== Email.toLowerCase();
    // Display the current registration error if:
    // 1. There is a validation error associated with this field.
    // 2. The user has not changed the email since the validation was last run.
    // The error is not displayed once the user has changed the email value as
    // the validation is not run until the user blurs the email input and the
    // error should be removed as as possible to avoid confusing the user.
    const shouldDisplayCurrentRegistrationError = !!(
        emailExistsInCurrentRegistration &&
        !hasChangedSincePreviousRegistrationValidation &&
        needsToBeVerified
    );
    // Only consider displaying the previous registration error if:
    // 1. If the validation logic has indicated that there is an error with the
    // hidden Verified_Email_Is_Unused_By_Previous_Registration field that we
    // use to store a boolean that indicates whether the email address has
    // already been used by a previous registration. This approach also allows
    // us to use this data as the single source of truth for this info enabling
    // us to dynamically add and remove this component from the UI if the user
    // toggles the "Is fundraising participant" value.
    // 2. If we can verify the the BE has had an opportunity to validate the
    // email address. This prevents the display of this error on page load.
    // 3. If the email address that the BE verified, is the same email address
    // that is currently entered into the Email input. This removes the error
    // from the UI as soon as the user modifies the value in the email input
    // as it would be misleading to display an error when the user has entered
    // an email address that we have not been able to check yet.
    const shouldDisplayPreviousRegistrationError = !!(
        emailExistsInPreviousRegistration &&
        !hasChangedSincePreviousRegistrationValidation &&
        needsToBeVerified
    );
    let shouldDisplayError = false;
    let loginToFundraisingDashboardLink;
    // Current Registration Errors always take precedence over previous
    // registration errors as it is not possible for a user to register an
    // email that has already been by a previous registration.
    if (shouldDisplayCurrentRegistrationError) {
        shouldDisplayError = true;
    } else if (shouldDisplayPreviousRegistrationError) {
        shouldDisplayError = true;

        // Open the view that the user can use to log in to their fundraising
        // dashboard in a new tab
        const handleLogInClick = () => {
            window.open(yourFundraisingDashboard, "_blank");
        };

        // If the error is associated with a previous registration then the
        // user needs to be given the ability to log in
        loginToFundraisingDashboardLink = isPrimaryRegistrant ? (
            <button
                type="button"
                className="account-registration-error__button -text--left"
                onClick={handleLogInClick}
            >
                <strong className="account-registration-error__button-text">
                    Log in to your fundraising dashboard here!
                </strong>
            </button>
        ) : null;
    }

    return (
        shouldDisplayError && (
            <div className="account-registration-error -width--100 -margin-top--5">
                <p className="-padding--10 -type--small -text--left">
                    <Icon
                        className="-margin-right--10, -type--large"
                        glyph="exclamation-circle-solid"
                        type="FontAwesome"
                    />
                    <strong>
                        {" "}
                        A registration already exists for this email.{" "}
                        {loginToFundraisingDashboardLink}
                    </strong>
                </p>
            </div>
        )
    );
};

AccountRegistrationError.propTypes = {
    Email: PropTypes.string,
    emailExistsInPreviousRegistration: PropTypes.bool.isRequired,
    emailExistsInCurrentRegistration: PropTypes.bool.isRequired,
    lastVerifiedEmail: PropTypes.string,
    Is_Nfp: PropTypes.bool,
    isPrimaryRegistrant: PropTypes.bool,
    Managed_By_Primary: PropTypes.bool,
    yourFundraisingDashboard: PropTypes.string,
};

export default AccountRegistrationError;
